import { useCallback } from "react";
import { type Color } from "@salestrak/ui";

import type { StageOption } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useStageMutations } from "#app/lib/hooks/use-stage-mutations";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/**
 * Inline stage editing shared by the board's stage column headers, the list's
 * stage group headers, and the workflow Stages settings rows: rename/recolor
 * via updateStage, gated on `manage.edit_workflows`. ONE call at the surface
 * level, never per column/group — a board is a list, and its headers are rows.
 */
export function useStageEditActions(
  /** The surface, for analytics: "board_column" or "table_group". */
  via: string,
) {
  const canEditStages = usePermission("manage.edit_workflows");
  const { updateStage } = useStageMutations();

  const renameStage = useCallback(
    (stageId: string, name: string) => {
      void updateStage(stageId, { name }, { via }).catch((err: unknown) =>
        toastMutationError(err, "Failed to rename stage"),
      );
    },
    [updateStage, via],
  );

  const recolorStage = useCallback(
    (stage: StageOption, color: Color) => {
      void updateStage(
        stage.stageId,
        { icon: { icon: stage.icon?.icon, color } },
        { via },
      ).catch((err: unknown) => toastMutationError(err, "Failed to update stage color"));
    },
    [updateStage, via],
  );

  return { canEditStages, renameStage, recolorStage };
}
